// Bridge Sudoku (Paul, 27 Oct 2026): random deals where two hands are seen (as shapes) and a few clues pin
// down the other two hands' full distribution. Every puzzle is checked by brute force to have ONE answer.
//
//   Defending: you are East, dummy North, declarer South, partner West. Seen: North + East.
//   Declaring: you are South, dummy North. Seen: North + South; West and East are the unknowns.
//
// Pure functions, no React, so it can be tested in node.

export const SUITS = ["S", "H", "D", "C"];
export const SYM = { S: "♠", H: "♥", D: "♦", C: "♣" };
const NAME = { S: "♠", H: "♥", D: "♦", C: "♣" }; // clue text uses the symbols (coloured on screen)
const ORD = ["", "first", "second", "third", "fourth"];

function shuffledShapes(rand) {
  const deck = [];
  for (let s = 0; s < 4; s++) for (let r = 0; r < 13; r++) deck.push(s);
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  // N, E, S, W - 13 cards each, counted by suit.
  const hands = { N: [0, 0, 0, 0], E: [0, 0, 0, 0], S: [0, 0, 0, 0], W: [0, 0, 0, 0] };
  ["N", "E", "S", "W"].forEach((seat, k) => deck.slice(k * 13, k * 13 + 13).forEach((s) => { hands[seat][s] += 1; }));
  return hands;
}

// Every split of the two unknown hands that fits the seen hands and all the clues.
export function solutions(known1, known2, clues, a, b) {
  const rest = [0, 1, 2, 3].map((i) => 13 - known1[i] - known2[i]);
  const out = [];
  for (let s = 0; s <= rest[0]; s++)
    for (let h = 0; h <= rest[1]; h++)
      for (let d = 0; d <= rest[2]; d++) {
        const c = 13 - s - h - d;
        if (c < 0 || c > rest[3]) continue;
        const A = [s, h, d, c];
        const B = rest.map((x, i) => x - A[i]);
        const hands = { [a]: A, [b]: B };
        if (clues.every((cl) => cl.test(hands))) out.push(hands);
      }
  return out;
}

const pick = (arr, rand) => arr[Math.floor(rand() * arr.length)];

// The clues the actual deal supports. `who` names a seat as the text should say it.
function candidateClues(hands, mode, trump, who, rand) {
  const t = SUITS.indexOf(trump);
  const unknown = mode === "defence" ? ["S", "W"] : ["W", "E"];
  const out = [];
  if (mode === "defence") {
    out.push({ key: "trumps", must: true, text: `Declarer has ${hands.S[t]} trumps.`, test: (h) => h.S[t] === hands.S[t] });
    // Partner's own long suit (an overcall).
    SUITS.forEach((s, i) => {
      if (i !== t && hands.W[i] >= 5) out.push({ key: `pl-${s}`, text: `Partner has ${hands.W[i]} ${NAME[s]}.`, test: (h) => h.W[i] === hands.W[i] });
    });
    // Partner's support for your long suit.
    SUITS.forEach((s, i) => {
      if (i !== t && hands.E[i] >= 5 && (hands.W[i] === 3 || hands.W[i] === 4))
        out.push({ key: `sup-${s}`, text: `You bid ${NAME[s]} and partner showed ${hands.W[i]}-card support.`, test: (h) => h.W[i] === hands.W[i] });
    });
  } else {
    // Declaring: an opponent's long suit, from the bidding.
    ["W", "E"].forEach((seat) => SUITS.forEach((s, i) => {
      if (i !== t && hands[seat][i] >= 5)
        out.push({ key: `long-${seat}-${s}`, text: `${who[seat]} has shown ${hands[seat][i]} ${NAME[s]}.`, test: (h) => h[seat][i] === hands[seat][i] });
    }));
  }
  // Someone shows out of trumps on round k (k-1 trumps, the other opponent has the rest).
  unknown.forEach((seat) => {
    const n = hands[seat][t];
    if (mode === "defence" && seat === "S") return; // declarer's trumps are given above
    if (n <= 2) {
      const round = n + 1;
      out.push({ key: `out-${seat}`, text: `${who[seat]} shows out on the ${ORD[round]} round of trumps.`, test: (h) => h[seat][t] === n });
    }
  });
  // A ruff: seat holds r cards in a side suit and ruffs round r+1 (needs a trump).
  unknown.forEach((seat) => SUITS.forEach((s, i) => {
    const r = hands[seat][i];
    if (i === t || r > 2 || hands[seat][t] === 0) return;
    const round = r + 1;
    const other = unknown.find((x) => x !== seat);
    // The other three hands must have had cards to play to the rounds before the ruff.
    if (["N", "E", "S", "W"].some((x) => x !== seat && hands[x][i] < r)) return;
    out.push({ key: `ruff-${seat}-${s}`,
      text: round === 1 ? `${who[seat]} ruffs the first round of ${NAME[s]}.` : `${who[seat]} ruffs the ${ORD[round]} round of ${NAME[s]}.`,
      test: (h) => h[seat][i] === r && h[other][i] >= r });
  }));
  // Shuffle the optional ones so each puzzle draws a different mix.
  const must = out.filter((c) => c.must);
  const opt = out.filter((c) => !c.must);
  for (let i = opt.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [opt[i], opt[j]] = [opt[j], opt[i]]; }
  return [...must, ...opt];
}

export function makePuzzle(mode = "defence", rand = Math.random) {
  for (let tries = 0; tries < 500; tries++) {
    const hands = shuffledShapes(rand);
    // Keep it to hands people meet: no suit longer than 7 (an 8-card suit looked odd in the flyer shot, 28 Oct 2026).
    if (Object.values(hands).some((h) => h.some((n) => n > 7))) continue;
    // Trumps: declarer's (South's) longest suit, 5+, with an 8+ card fit with dummy.
    const order = [0, 1, 2, 3].sort((x, y) => hands.S[y] - hands.S[x] || hands.N[y] - hands.N[x]);
    const ti = order[0];
    if (hands.S[ti] < 5 || hands.S[ti] + hands.N[ti] < 8) continue;
    const trump = SUITS[ti];
    const who = mode === "defence"
      ? { N: "Dummy", E: "You", S: "Declarer", W: "Partner" }
      : { N: "Dummy", E: "East", S: "You", W: "West" };
    const [k1, k2] = mode === "defence" ? ["N", "E"] : ["N", "S"];
    const [a, b] = mode === "defence" ? ["S", "W"] : ["W", "E"];
    const cands = candidateClues(hands, mode, trump, who, rand);
    const chosen = [];
    for (const c of cands) {
      chosen.push(c);
      if (solutions(hands[k1], hands[k2], chosen, a, b).length === 1) break;
    }
    if (solutions(hands[k1], hands[k2], chosen, a, b).length !== 1) continue;
    // Drop any optional clue the answer does not need, so every clue is doing work.
    for (let i = chosen.length - 1; i >= 0; i--) {
      if (chosen[i].must) continue;
      const without = chosen.filter((_, j) => j !== i);
      if (solutions(hands[k1], hands[k2], without, a, b).length === 1) chosen.splice(i, 1);
    }
    if (chosen.length > 4) continue;
    if (mode === "declarer" && chosen.length < 2) continue;
    // The contract, 2 to 4 of the trump suit, so it is plain what trumps are (Paul, 28 Oct 2026).
    const level = 2 + Math.floor(rand() * 3);
    return { mode, trump, level, contract: `${level}${SYM[trump]}`, hands, who, seen: [k1, k2], unknown: [a, b], clues: chosen.map((c) => c.text) };
  }
  return null;
}

export const pickMode = (setting, rand = Math.random) => (setting === "mix" ? pick(["defence", "declarer"], rand) : setting);
